/**
 * Occupied Clothing - Liked Products / Favorites System
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'occupied_liked_products_v1';

  class LikedProductsManager {
    constructor() {
      this.items = this.load();
      this.init();
    }

    load() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.error('Error loading liked products from localStorage:', e);
        return [];
      }
    }

    save() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (e) {
        console.error('Error saving liked products to localStorage:', e);
      }
      this.notify();
    }

    notify() {
      this.updateBadges();
      this.updateButtons();
      this.renderDrawer();
      window.dispatchEvent(new CustomEvent('liked:updated', { detail: { items: this.items } }));
    }

    isLiked(productId) {
      if (!productId) return false;
      return this.items.some(item => String(item.id) === String(productId));
    }

    toggle(productData, buttonEl) {
      if (!productData || !productData.id) return;
      const id = String(productData.id);
      const index = this.items.findIndex(item => String(item.id) === id);

      if (index > -1) {
        // Remove item
        this.items.splice(index, 1);
        if (buttonEl) buttonEl.classList.remove('is-liked');
      } else {
        // Add item
        this.items.unshift({
          id: id,
          title: productData.title || '',
          handle: productData.handle || '',
          price: productData.price || '',
          compare_at_price: productData.compare_at_price || '',
          image: productData.image || '',
          url: productData.url || '',
          available: productData.available !== false,
          variant_id: productData.variant_id || '',
          added_at: Date.now()
        });
        if (buttonEl) buttonEl.classList.add('is-liked');
      }

      this.save();
    }

    remove(productId) {
      const id = String(productId);
      this.items = this.items.filter(item => String(item.id) !== id);
      this.save();
    }

    clearAll() {
      this.items = [];
      this.save();
    }

    updateBadges() {
      const count = this.items.length;
      const badges = document.querySelectorAll('.liked-header-badge, [data-liked-count]');
      badges.forEach(badge => {
        badge.textContent = count;
        if (count > 0) {
          badge.classList.add('has-items');
          badge.classList.remove('pulse');
          void badge.offsetWidth; // trigger reflow
          badge.classList.add('pulse');
        } else {
          badge.classList.remove('has-items');
        }
      });
    }

    updateButtons() {
      const buttons = document.querySelectorAll('[data-liked-btn]');
      buttons.forEach(btn => {
        const id = btn.getAttribute('data-liked-id');
        const textEl = btn.querySelector('.liked-btn-text');
        if (this.isLiked(id)) {
          btn.classList.add('is-liked');
          btn.setAttribute('aria-label', 'Remove from Liked');
          if (textEl) textEl.textContent = 'Liked';
        } else {
          btn.classList.remove('is-liked');
          btn.setAttribute('aria-label', 'Save to Liked');
          if (textEl) textEl.textContent = 'Save to Liked';
        }
      });
    }

    renderDrawer() {
      const container = document.getElementById('liked-drawer-items-container');
      const emptyState = document.getElementById('liked-drawer-empty-state');
      const countPill = document.getElementById('liked-drawer-count-pill');
      const clearBtn = document.getElementById('liked-drawer-clear-btn');

      if (!container || !emptyState) return;

      const count = this.items.length;
      if (countPill) countPill.textContent = count;

      if (count === 0) {
        container.innerHTML = '';
        container.style.display = 'none';
        emptyState.style.display = 'flex';
        if (clearBtn) clearBtn.style.display = 'none';
        return;
      }

      emptyState.style.display = 'none';
      container.style.display = 'flex';
      if (clearBtn) clearBtn.style.display = 'block';

      let html = '';
      this.items.forEach(item => {
        const hasCompare = item.compare_at_price && item.compare_at_price !== item.price;
        html += `
          <div class="liked-item-card" data-liked-item-id="${item.id}">
            <a href="${item.url || '#'}" class="liked-item__image-wrap">
              ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : `<div style="width:100%;height:100%;background:#eee;"></div>`}
            </a>
            <div class="liked-item__details">
              <div class="liked-item__top">
                <a href="${item.url || '#'}" class="liked-item__title">${item.title}</a>
                <button type="button" class="liked-item__remove-btn" data-liked-remove-id="${item.id}" aria-label="Remove item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div class="liked-item__pricing">
                <span class="liked-item__price">${item.price}</span>
                ${hasCompare ? `<span class="liked-item__compare-price">${item.compare_at_price}</span>` : ''}
              </div>
              <div class="liked-item__actions">
                ${item.variant_id && item.available ? `
                  <button type="button" class="liked-item__add-cart-btn" data-liked-add-cart="${item.variant_id}">
                    Add to Cart
                  </button>
                ` : `
                  <a href="${item.url || '#'}" class="liked-item__add-cart-btn" style="text-decoration:none;">
                    View Options
                  </a>
                `}
                <a href="${item.url || '#'}" class="liked-item__view-btn" aria-label="View product">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        `;
      });

      container.innerHTML = html;
    }

    openDrawer() {
      const drawer = document.getElementById('liked-products-drawer');
      const overlay = document.getElementById('liked-drawer-overlay');
      if (drawer && overlay) {
        this.renderDrawer();
        drawer.classList.add('is-active');
        overlay.classList.add('is-active');
        document.body.style.overflow = 'hidden';
      }
    }

    closeDrawer() {
      const drawer = document.getElementById('liked-products-drawer');
      const overlay = document.getElementById('liked-drawer-overlay');
      if (drawer && overlay) {
        drawer.classList.remove('is-active');
        overlay.classList.remove('is-active');
        document.body.style.overflow = '';
      }
    }

    async addToCart(variantId, btn) {
      if (!variantId) return;
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Adding...';
      }

      try {
        const res = await fetch(window.Shopify ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }]
          })
        });

        if (res.ok) {
          if (btn) {
            btn.textContent = 'Added!';
            setTimeout(() => {
              btn.textContent = 'Add to Cart';
              btn.disabled = false;
            }, 1500);
          }

          // Trigger cart updates if theme supports it
          try {
            const cartRes = await fetch(window.Shopify ? window.Shopify.routes.root + 'cart.js' : '/cart.js');
            const cartData = await cartRes.json();
            const cartBadges = document.querySelectorAll('#urban-cart-count, [id^="urban-cart-counter-"], cart-bubble');
            cartBadges.forEach(b => {
              if (b.tagName.toLowerCase() === 'cart-bubble') {
                b.textContent = cartData.item_count;
              } else {
                b.textContent = cartData.item_count;
              }
            });

            // If cart drawer trigger exists, dispatch event
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartData } }));
          } catch (err) {}
        } else {
          if (btn) {
            btn.textContent = 'Error';
            btn.disabled = false;
          }
        }
      } catch (err) {
        console.error('Cart add error:', err);
        if (btn) {
          btn.textContent = 'Error';
          btn.disabled = false;
        }
      }
    }

    init() {
      // Cross-tab sync
      window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY) {
          this.items = this.load();
          this.notify();
        }
      });

      // Handle button clicks delegation
      document.addEventListener('click', (e) => {
        // Toggle Like Button
        const likeBtn = e.target.closest('[data-liked-btn]');
        if (likeBtn) {
          e.preventDefault();
          e.stopPropagation();
          const productData = {
            id: likeBtn.getAttribute('data-liked-id'),
            handle: likeBtn.getAttribute('data-liked-handle'),
            title: likeBtn.getAttribute('data-liked-title'),
            price: likeBtn.getAttribute('data-liked-price'),
            compare_at_price: likeBtn.getAttribute('data-liked-compare-price'),
            image: likeBtn.getAttribute('data-liked-image'),
            url: likeBtn.getAttribute('data-liked-url'),
            variant_id: likeBtn.getAttribute('data-liked-variant-id'),
            available: likeBtn.getAttribute('data-liked-available') !== 'false'
          };
          this.toggle(productData, likeBtn);
          return;
        }

        // Open Liked Drawer Triggers
        const openTrigger = e.target.closest('[data-liked-open-drawer], .header-actions__wishlist, .urban-wishlist-btn, .wishlist-page-widget, [href="#hero-wishlist"], [href="/pages/wishlist"]');
        if (openTrigger) {
          e.preventDefault();
          e.stopPropagation();
          this.openDrawer();
          return;
        }

        // Close Liked Drawer Triggers
        const closeTrigger = e.target.closest('[data-liked-close-drawer], #liked-drawer-overlay');
        if (closeTrigger) {
          e.preventDefault();
          e.stopPropagation();
          this.closeDrawer();
          return;
        }

        // Remove item from Drawer
        const removeBtn = e.target.closest('[data-liked-remove-id]');
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation();
          const id = removeBtn.getAttribute('data-liked-remove-id');
          this.remove(id);
          return;
        }

        // Clear All Liked Items
        const clearBtn = e.target.closest('#liked-drawer-clear-btn');
        if (clearBtn) {
          e.preventDefault();
          e.stopPropagation();
          if (confirm('Are you sure you want to clear all liked items?')) {
            this.clearAll();
          }
          return;
        }

        // Add to Cart from Drawer
        const addCartBtn = e.target.closest('[data-liked-add-cart]');
        if (addCartBtn) {
          e.preventDefault();
          e.stopPropagation();
          const variantId = addCartBtn.getAttribute('data-liked-add-cart');
          this.addToCart(variantId, addCartBtn);
          return;
        }
      });

      // ESC key closes drawer
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeDrawer();
        }
      });

      // Initial DOM sync
      document.addEventListener('DOMContentLoaded', () => {
        this.updateBadges();
        this.updateButtons();
      });

      // Page view transition / dynamic load observer
      const observer = new MutationObserver(() => {
        this.updateButtons();
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Run immediate update
      this.updateBadges();
      this.updateButtons();
    }
  }

  // Expose global instance
  window.LikedProducts = new LikedProductsManager();
})();
